import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, Send, Paperclip, Search, ShieldAlert, Ban, Loader2, X, ChevronRight, ArchiveIcon } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

// ---------- header/list helpers -----------------------------------------
/** Return the other participant object (as enriched by the API) for a
 *  direct/support conversation, or null for multi-party ones. */
function counterpartyOf(conv, me) {
  if (!conv || !me) return null;
  const parts = conv.participants || [];
  if (parts.length === 0) return null;
  const others = parts.filter((p) => p && p.id !== me.id);
  return others[0] || null;
}
/** Human label for the header/list row — prefer the counterparty's brand
 *  name (business/agency) or account display name, then fall back to the
 *  conversation subject, then to a generic label. */
function displayLabel(conv, other) {
  if (other) {
    return other.provider_display_name || other.display_name || "GCW member";
  }
  if (conv?.subject) return conv.subject;
  if (conv?.kind === "booking") return "Booking conversation";
  if (conv?.kind === "order") return "Order conversation";
  if (conv?.kind === "request") return "Request conversation";
  if (conv?.kind === "support") return "GCW Support";
  return "Conversation";
}
/** Small avatar with a tinted initial fallback — used in inbox rows + header. */
function Avatar({ url, name, size = 32 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const style = { width: size, height: size, minWidth: size };
  if (url) {
    return <img src={url} alt="" style={style} className="rounded-full object-cover shrink-0" />;
  }
  return (
    <div style={style} className="rounded-full bg-[#165a71]/10 text-[#165a71] font-semibold flex items-center justify-center shrink-0 text-sm">
      {initial}
    </div>
  );
}

/**
 * Live Messages Center (Phase 5).
 * - Loads conversation list from /messages/conversations.
 * - Streams new messages / typing via WebSocket at /api/ws/messages.
 * - Uses /messages/conversations/{id}/send (Phase 5 msg2 router) so attachments
 *   are supported.
 * - Contact-info masking is enforced server-side; the client just renders
 *   whatever body was persisted.
 */
export default function MessagesCenterPage() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [showBlocks, setShowBlocks] = useState(false);
  useDocumentTitle("Messages");

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConvs(data || []);
      if (!active && data?.length) setActive(data[0]);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link support: if the URL has ?conv=<id>, auto-select that conversation
  // (used by "Message" buttons on provider profiles / service detail pages).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const convId = searchParams.get("conv");
    if (!convId || convs.length === 0) return;
    const found = convs.find((c) => c.id === convId);
    if (found && (!active || active.id !== convId)) setActive(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, convs]);

  const loadMsgs = useCallback(async (conv) => {
    if (!conv?.id) return;
    try {
      const { data } = await api.get(`/messages/conversations/${conv.id}`);
      setMsgs(data.messages || []);
      // Mark read via API + WS (both keep the badge in sync)
      api.post(`/messages/conversations/${conv.id}/read`).catch(() => {});
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { if (active) loadMsgs(active); }, [active, loadMsgs]);

  // WS handler — also dispatches a CustomEvent so nested components
  // (ConversationView) can react to typing/typing indicators without
  // needing their own socket.
  const onWSMessage = useCallback((msg) => {
    try { window.dispatchEvent(new CustomEvent("gcw:ws", { detail: msg })); } catch (_) {}
    if (msg?.type === "message" && msg.message) {
      // If it's for the currently open conversation, append.
      if (active && msg.conversation_id === active.id) {
        setMsgs((prev) => (prev.some((m) => m.id === msg.message.id) ? prev : [...prev, msg.message]));
        api.post(`/messages/conversations/${active.id}/read`).catch(() => {});
      }
      // Refresh conv list to update preview/unread
      loadConvs();
    }
  }, [active, loadConvs]);
  const { send: wsSend } = useWebSocket({ onMessage: onWSMessage });

  useEffect(() => {
    if (active) wsSend({ type: "read", conversation_id: active.id });
  }, [active, wsSend]);

  const doSearch = async () => {
    const query = q.trim();
    if (query.length < 2) { setSearchResults(null); return; }
    try {
      const { data } = await api.get("/messages/search", { params: { q: query } });
      setSearchResults(data.results || []);
    } catch (err) {
      toast.error("Search failed");
    }
  };
  useEffect(() => {
    if (!q.trim()) setSearchResults(null);
  }, [q]);

  return (
    <DashboardShell>
      <div className="max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Messages</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">All your threads, one inbox.</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Real-time chat with contact-info masking until a job is booked.</p>
          </div>
          <button onClick={() => setShowBlocks(true)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium" data-testid="messages-manage-blocks">
            <Ban className="h-4 w-4" /> Blocked users
          </button>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#252A34]/50" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
              placeholder="Search messages…"
              className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-[#165a71]"
              data-testid="messages-search"
            />
          </div>
          <button onClick={doSearch} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="messages-search-btn">Search</button>
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : convs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <MessageSquare className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No messages yet.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Once you book, order, or receive a proposal, message threads land here.</div>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-4 rounded-2xl border border-slate-200 bg-white divide-y max-h-[75vh] overflow-y-auto" data-testid="inbox-list">
              {searchResults ? (
                <SearchList results={searchResults} convs={convs} onOpen={(m) => {
                  const conv = convs.find((c) => c.id === m.conversation_id);
                  if (conv) { setActive(conv); setSearchResults(null); setQ(""); }
                }} />
              ) : (
                convs.map((c) => {
                  const other = counterpartyOf(c, user);
                  const label = displayLabel(c, other);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setActive(c)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${active?.id === c.id ? "bg-[#165a71]/5" : ""}`}
                      data-testid={`inbox-conv-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar url={other?.picture} name={label} size={28} />
                          <div className="font-display font-semibold text-[#071b22] text-sm truncate" data-testid={`inbox-conv-title-${c.id}`}>{label}</div>
                        </div>
                        {(c.unread_by?.[user?.id] || 0) > 0 && <span className="rounded-full bg-[#165a71] text-white text-[10px] px-2 py-0.5 font-semibold">{c.unread_by[user.id]}</span>}
                      </div>
                      <div className="text-xs text-[#252A34]/60 mt-1 truncate">{c.last_message_preview || "—"}</div>
                      <div className="text-[10px] uppercase text-[#252A34]/50 tracking-wider mt-1">{c.last_message_at ? timeAgo(c.last_message_at) : "New"}</div>
                    </button>
                  );
                })
              )}
            </div>
            <div className="lg:col-span-8">
              {active ? (
                <ConversationView
                  conv={active}
                  msgs={msgs}
                  me={user}
                  wsSend={wsSend}
                  onSent={(m) => setMsgs((prev) => [...prev, m])}
                  onArchived={() => {
                    setActive(null);
                    loadConvs();
                  }}
                  onBlocked={() => loadConvs()}
                />
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-sm text-[#252A34]/60 text-center h-[520px] flex items-center justify-center">
                  Select a conversation.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showBlocks && <BlocksDialog onClose={() => setShowBlocks(false)} />}
    </DashboardShell>
  );
}

function timeAgo(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function ConversationView({ conv, msgs, me, wsSend, onSent, onArchived, onBlocked }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [reporting, setReporting] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const otherId = (conv.participant_ids || []).find((p) => p !== me?.id);
  const other = counterpartyOf(conv, me);
  const headerLabel = displayLabel(conv, other);
  const headerSubline = other?.provider_display_name && other?.display_name && other.provider_display_name !== other.display_name
    ? other.display_name  // show account holder when provider-brand name differs
    : (conv.subject && conv.subject !== headerLabel ? conv.subject : null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs.length]);

  // Clear stale typing indicators after 3s
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setTypingUsers((tu) => {
        const next = { ...tu };
        Object.keys(next).forEach((k) => { if (now - next[k] > 3000) delete next[k]; });
        return next;
      });
    }, 1500);
    return () => clearInterval(t);
  }, []);

  const upload = async (files) => {
    setUploading(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "message");
      fd.append("conversation_id", conv.id);
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, name: data.original_filename, url: data.url, content_type: data.content_type, size: data.size_bytes });
      } catch (err) {
        toast.error(err?.response?.data?.error || `Could not upload ${f.name}`);
      }
    }
    setAttachments((prev) => [...prev, ...added]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) upload(files);
  };

  const send = async () => {
    const b = body.trim();
    if (!b && attachments.length === 0) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/conversations/${conv.id}/send`, {
        body: b || null,
        attachment_ids: attachments.map((a) => a.id),
      });
      onSent(data);
      setBody("");
      setAttachments([]);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this conversation from your inbox?")) return;
    try {
      await api.post(`/messages/conversations/${conv.id}/archive`);
      onArchived?.();
    } catch (err) { toast.error("Could not archive"); }
  };

  const block = async () => {
    if (!otherId) return;
    if (!window.confirm("Block this user? You'll stop receiving their messages.")) return;
    try {
      await api.post("/messages/blocks", { user_id: otherId });
      toast.success("User blocked");
      onBlocked?.();
    } catch (err) { toast.error(err?.response?.data?.error || "Could not block"); }
  };

  // Typing indicator send (throttled)
  const lastTypingRef = useRef(0);
  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current > 1500) {
      wsSend({ type: "typing", conversation_id: conv.id });
      lastTypingRef.current = now;
    }
  };

  // Listen for typing on the same conversation via WS (we already do that in parent — this
  // convo re-mounts on switch, so wire it here through parent's onWSMessage via lifting):
  // parent already refreshes on message; typing needs an extra hook. Attach a listener.
  useEffect(() => {
    // Piggyback on window because our WS hook is scoped to parent. Simple version:
    // We rely on the parent WS hook forwarding via a global CustomEvent for typing.
    const handler = (e) => {
      const d = e.detail;
      if (d?.type === "typing" && d.conversation_id === conv.id && d.user_id !== me?.id) {
        setTypingUsers((tu) => ({ ...tu, [d.user_id]: Date.now() }));
      }
    };
    window.addEventListener("gcw:ws", handler);
    return () => window.removeEventListener("gcw:ws", handler);
  }, [conv.id, me?.id]);

  const otherTyping = Object.keys(typingUsers).length > 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white flex flex-col h-[75vh]" data-testid="message-thread">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
        <Avatar url={other?.picture} name={headerLabel} size={40} />
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-[#071b22] truncate" data-testid="conv-header-name">
            {other?.provider_slug ? (
              <Link to={`/providers/${other.provider_slug}`} className="hover:text-[#165a71]">{headerLabel}</Link>
            ) : headerLabel}
          </div>
          <div className="text-xs text-[#252A34]/60 truncate" data-testid="conv-header-sub">
            {headerSubline || "All discussion stays on this thread. Contacts are masked until you book."}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={archive} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-[#252A34]/80 hover:bg-slate-50" data-testid="conv-archive">Archive</button>
          {otherId && (
            <button onClick={block} className="rounded-full border border-rose-200 text-rose-600 bg-white px-3 py-1.5 text-xs" data-testid="conv-block-user">
              <Ban className="h-3 w-3 inline mr-1" /> Block
            </button>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="message-list">
        {msgs.length === 0 ? (
          <div className="text-center py-16 text-sm text-[#252A34]/50">Start the conversation — say hi.</div>
        ) : (
          msgs.map((m) => <Bubble key={m.id} msg={m} me={me?.id} onReport={() => setReporting(m)} />)
        )}
        {otherTyping && (
          <div className="text-xs text-[#252A34]/50 italic ml-2" data-testid="typing-indicator">Someone is typing…</div>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex flex-wrap gap-2" data-testid="attachment-strip">
          {attachments.map((a) => (
            <div key={a.id} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-3 pr-1 py-1 text-xs">
              <span className="truncate max-w-[10rem]">{a.name}</span>
              <button onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} className="rounded-full p-1 hover:bg-slate-100" aria-label="Remove attachment" data-testid={`attachment-remove-${a.id}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-100 p-3 flex items-end gap-2">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={onFile} data-testid="attachment-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 w-10 rounded-full border border-slate-200 flex items-center justify-center text-[#252A34]/70" data-testid="attachment-btn" aria-label="Attach">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
        <textarea
          rows={1}
          value={body}
          onChange={(e) => { setBody(e.target.value); notifyTyping(); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…"
          className="flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#165a71] focus:bg-white"
          data-testid="message-input"
        />
        <button
          disabled={sending || (!body.trim() && attachments.length === 0)}
          onClick={send}
          className="rounded-full bg-[#165a71] text-white h-10 w-10 flex items-center justify-center hover:bg-[#11485a] disabled:opacity-40"
          data-testid="message-send-btn"
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>

      {reporting && <ReportDialog msg={reporting} onClose={() => setReporting(null)} />}
    </div>
  );
}

function Bubble({ msg, me, onReport }) {
  const isMine = msg.sender_id === me;
  if (msg.kind === "system") {
    return (
      <div className="my-3 text-center">
        <div className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] uppercase tracking-wider text-[#252A34]/60" data-testid="system-message">
          {msg.body}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} group`} data-testid={`message-bubble-${isMine ? "mine" : "them"}`}>
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm space-y-1 ${
        isMine ? "bg-[#165a71] text-white" : "bg-slate-100 text-[#071b22]"
      }`} data-testid={`message-${msg.id}`}>
        {msg.body && <div className="whitespace-pre-line">{msg.body}</div>}
        {(msg.attachments || []).length > 0 && (
          <div className="mt-1 flex flex-col gap-1">
            {msg.attachments.map((a, i) => {
              const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/files/${a.file_id}?auth=${encodeURIComponent(window.localStorage.getItem("gcw_bearer") || "")}`;
              const isImage = (a.content_type || "").startsWith("image/");
              if (isImage) {
                return <a key={i} href={url} target="_blank" rel="noreferrer" className="block"><img src={url} alt={a.name} className="rounded-lg max-w-[220px] max-h-[220px] object-cover" /></a>;
              }
              return <a key={i} href={url} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1.5 text-xs underline ${isMine ? "text-white/90" : "text-[#165a71]"}`}><Paperclip className="h-3 w-3" /> {a.name}</a>;
            })}
          </div>
        )}
        <div className={`text-[10px] mt-1 ${isMine ? "text-white/70" : "text-[#252A34]/60"} flex items-center gap-2`}>
          <span>{msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
          {!isMine && (
            <button onClick={onReport} className="opacity-0 group-hover:opacity-100 underline" data-testid={`report-msg-${msg.id}`}>
              <ShieldAlert className="h-3 w-3 inline" /> report
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportDialog({ msg, onClose }) {
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/messages/messages/${msg.id}/report`, { reason, details: details || null });
      toast.success("Reported — moderators will review.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not report");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4" data-testid="report-dialog">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 border border-slate-200 shadow-2xl">
        <div className="font-display text-lg text-[#071b22] font-semibold">Report this message</div>
        <div className="text-xs text-[#252A34]/70 mt-1">A moderator will review it against our community rules.</div>
        <div className="mt-4">
          <label className="text-xs text-[#252A34]/70">Reason</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="report-reason">
            <option value="harassment">Harassment</option>
            <option value="spam">Spam</option>
            <option value="scam">Scam or fraud</option>
            <option value="contact_info">Sharing contact info</option>
            <option value="prohibited">Prohibited content</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="mt-3">
          <label className="text-xs text-[#252A34]/70">Details (optional)</label>
          <textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="report-details" />
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium">Cancel</button>
          <button onClick={submit} disabled={saving} className="rounded-full bg-rose-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="report-submit">
            {saving ? "Sending…" : "Send report"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SearchList({ results, convs, onOpen }) {
  if (results.length === 0) return <div className="p-6 text-sm text-[#252A34]/60 text-center">No results.</div>;
  return (
    <>
      {results.map((m) => (
        <button key={m.id} onClick={() => onOpen(m)} className="w-full text-left px-4 py-3 hover:bg-slate-50" data-testid={`search-result-${m.id}`}>
          <div className="text-xs text-[#252A34]/60">{convs.find((c) => c.id === m.conversation_id)?.subject || "Conversation"}</div>
          <div className="text-sm text-[#071b22] truncate">{m.body}</div>
        </button>
      ))}
    </>
  );
}

function BlocksDialog({ onClose }) {
  const [blocks, setBlocks] = useState([]);
  useEffect(() => {
    api.get("/messages/blocks").then((r) => setBlocks(r.data || [])).catch(() => {});
  }, []);
  const unblock = async (uid) => {
    try {
      await api.delete(`/messages/blocks/${uid}`);
      setBlocks((prev) => prev.filter((b) => b.blocked_id !== uid));
      toast.success("Unblocked");
    } catch (err) { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4" data-testid="blocks-dialog">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 border border-slate-200 shadow-2xl">
        <div className="font-display text-lg text-[#071b22] font-semibold">Blocked users</div>
        {blocks.length === 0 ? (
          <div className="mt-4 text-sm text-[#252A34]/60">You haven't blocked anyone.</div>
        ) : (
          <ul className="mt-4 divide-y border border-slate-200 rounded-xl">
            {blocks.map((b) => (
              <li key={b.id} className="p-3 flex items-center justify-between text-sm">
                <span className="font-mono text-xs">{b.blocked_id}</span>
                <button onClick={() => unblock(b.blocked_id)} className="text-xs text-[#165a71] font-medium" data-testid={`unblock-${b.blocked_id}`}>Unblock</button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium">Close</button>
        </div>
      </div>
    </div>
  );
}
